import { useEffect, useState } from "react";
import { Box, Button, Radio, RadioGroup, Typography, useTheme } from "@mui/material";
import AdaptiveDialog, { dialogPrimaryButtonSx, dialogQuietButtonSx } from "../common/AdaptiveDialog";
import { useTouchLayout } from "../../hooks/useTouchLayout";
import { hapticSelection } from "../../lib/haptics";
import { alertSubjectLine, levelOptionsForTopic } from "./subscriptionSelectors";
import { SubscriptionLevel } from "./types";

interface AlertLevelDialogProps {
  open: boolean;
  onClose: () => void;
  topic: string;
  /** What the alert follows, e.g. the company name. */
  subject: string;
  /** The level alerts are on at, or null while they are off. */
  level: SubscriptionLevel | null;
  onSave: (level: SubscriptionLevel) => void;
  /** Offered only while alerts are on. */
  onTurnOff?: () => void;
}

/**
 * How much the user wants to hear about one subscription — shared by the
 * "Get alerts" button and the rows on Manage alerts. A choice, then a confirm:
 * nothing changes until the primary button is pressed. The options come from
 * levelOptionsForTopic, so a new level (a model-judged "Meaningful only", say)
 * is one more entry there, not a change here.
 */
const AlertLevelDialog = ({
  open,
  onClose,
  topic,
  subject,
  level,
  onSave,
  onTurnOff,
}: AlertLevelDialogProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isTouch = useTouchLayout();
  const options = levelOptionsForTopic(topic);
  const [choice, setChoice] = useState<SubscriptionLevel>(level ?? options[0].level);

  // Every opening starts from what is saved, not from an abandoned choice.
  useEffect(() => {
    if (open) setChoice(level ?? levelOptionsForTopic(topic)[0].level);
  }, [open, level, topic]);

  const isOn = level !== null;
  const unchanged = isOn && choice === level;
  const quietHover = isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)";


  return (
    <AdaptiveDialog
      open={open}
      onClose={onClose}
      title="Alert me about"
      subtitle={alertSubjectLine(topic, subject)}
      maxWidth="xs"
      divided
      actions={
        <>
          <Button
            onClick={() => onSave(choice)}
            disabled={unchanged}
            variant="contained"
            fullWidth={isTouch}
            disableElevation
            sx={{ ...dialogPrimaryButtonSx(isDark, isTouch), order: isTouch ? 0 : 2 }}
          >
            {isOn && "Save"}
            {!isOn && "Turn on alerts"}
          </Button>
          <Button
            onClick={onClose}
            fullWidth={isTouch}
            sx={{ ...dialogQuietButtonSx(isDark, isTouch), order: 1 }}
          >
            Cancel
          </Button>
          {isOn && onTurnOff && (
            <Button
              onClick={onTurnOff}
              fullWidth={isTouch}
              sx={{
                ...dialogQuietButtonSx(isDark, isTouch),
                // Pointer: pushed to the far left, away from Save. Touch: last.
                order: isTouch ? 2 : 0,
                mr: isTouch ? 0 : "auto",
                color: theme.palette.error.main,
                "&:hover": {
                  color: theme.palette.error.main,
                  backgroundColor: "rgba(239, 68, 68, 0.08)",
                },
              }}
            >
              Turn off alerts
            </Button>
          )}
        </>
      }
    >
      <RadioGroup
        aria-label="Alert me about"
        value={choice}
        onChange={(event) => {
          hapticSelection();
          setChoice(event.target.value as SubscriptionLevel);
        }}
        sx={{ gap: 1 }}
      >
        {options.map((option) => {
          const selected = option.level === choice;
          return (
            <Box
              key={option.level}
              component="label"
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                px: 1.75,
                py: 1.5,
                borderRadius: 2,
                cursor: "pointer",
                // The house selected row: a green outline and a faint green wash.
                border: `1px solid ${selected ? "rgba(34, 197, 94, 0.45)" : theme.palette.divider}`,
                backgroundColor: selected ? "rgba(34, 197, 94, 0.08)" : "transparent",
                WebkitTapHighlightColor: "transparent",
                transition: "background-color 0.1s ease, border-color 0.1s ease",
                "&:hover": {
                  backgroundColor: selected ? "rgba(34, 197, 94, 0.12)" : quietHover,
                },
                "&:has(.Mui-focusVisible)": {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: 2,
                },
                "@media (prefers-reduced-motion: reduce)": { transition: "none" },
              }}
            >
              <Radio
                value={option.level}
                size="small"
                disableRipple
                sx={{
                  p: 0,
                  mt: "2px",
                  color: theme.palette.text.secondary,
                  "&.Mui-checked": { color: theme.palette.primary.main },
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "0.9375rem",
                    fontWeight: selected ? 600 : 500,
                    lineHeight: 1.35,
                    color: theme.palette.text.primary,
                  }}
                >
                  {option.label}
                </Typography>
                {option.detail && (
                  <Typography
                    sx={{
                      mt: 0.25,
                      fontSize: "0.8125rem",
                      lineHeight: 1.45,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {option.detail}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </RadioGroup>
    </AdaptiveDialog>
  );
};

export default AlertLevelDialog;
